import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogPrimaryShortcut,
} from './ui/dialog';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';

interface MarkAsDoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (message: string | null) => void;
  isLoading?: boolean;
}

export const MarkAsDoneModal: React.FC<MarkAsDoneModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const { t } = useLocale();
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setMessage('');
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    onSubmit(message.trim() || null);
    setMessage('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isLoading && onClose()}>
      <DialogContent className="max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('mark_feedback_as_done')}</DialogTitle>
          <DialogDescription>{t('mark_feedback_as_done_description')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <label
            className="mb-2 block text-sm font-medium text-foreground"
            htmlFor="mark-done-message"
          >
            {t('message_to_user')} ({t('optional')})
          </label>
          <textarea
            id="mark-done-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('message_to_user_placeholder')}
            disabled={isLoading}
            autoFocus
            className="mb-4 w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm font-inherit resize-y outline-none focus:border-[var(--scribane-black)] disabled:cursor-not-allowed disabled:opacity-50"
          />
          <DialogFooter className="sm:justify-end">
            <CancelButton type="button" onClick={onClose} disabled={isLoading} />
            <Button
              type="submit"
              disabled={isLoading}
              dialogPrimary
              className="inline-flex items-center gap-1.5"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('saving')}
                </>
              ) : (
                <>
                  {t('mark_as_done')}
                  <DialogPrimaryShortcut className="ml-0.5" />
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
